import { Component, inject, OnInit, resource } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule, AsyncPipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, catchError, firstValueFrom, forkJoin, map, of } from 'rxjs';
import { Bundle, ChargeItem, Dosage, Invoice, Medication, MedicationDispense, MedicationRequest, PaymentReconciliation } from 'fhir/r4';
import { StateService } from '../shared/state.service';
import { AuthService, capacityObject } from '../shared/auth/auth.service';
import { ErrorService } from '../shared/error.service';
import { UtilityService } from '../shared/utility.service';
import { DetailActionButton, DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { ChipsDirective } from '../chips.directive';
import { NaPipe } from '../shared/na.pipe';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { backendEndPointToken } from '../app.config';
import { HttpClient } from '@angular/common/http';
import { MedicationDispenseFieldFilterService } from './medication-dispense-field-filter.service';
import { MedicationDispenseUtilityService } from './medication-dispense-utility.service';
import { NewMedicationDispenseComponent } from './new-medication-dispense/new-medication-dispense.component';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'app-medication-dispense',
    standalone: true,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatTableModule,
        MatTooltipModule,
        CommonModule,
        AsyncPipe,
        NaPipe,
        ReferenceDisplayDirective,
        EmptyStateComponent,
        ChipsDirective,
        TableHeaderComponent
    ],
    templateUrl: './medication-dispense.component.html',
    styleUrls: ['../medicine-requests/medicine-requests.component.scss', './medication-dispense.component.scss']
})
export class MedicationDispenseComponent implements OnInit {
    medDispenseTableFilterArray = new Map<string, string[]>([
        ['status', ['completed', 'in-progress', 'cancelled', 'entered-in-error']],
        ['medication', ['Amoxicillin', 'Paracetamol', 'Metformin', 'Ibuprofen']]
    ]);
    medDispenseFiltersFormControlObject: Record<string, FormGroup> = {};
    private fb = inject(FormBuilder);
    dialog = inject(MatDialog);
    stateService = inject(StateService);
    auth = inject(AuthService);
    errorService = inject(ErrorService);
    utilityService = inject(UtilityService);
    capacityObject = capacityObject;

    tableDataLevel2 = new BehaviorSubject<MedicationDispense[]>([]);
    tableDataSource = new MatTableDataSource<MedicationDispense>();
    displayedColumns = ['whenHandedOver', 'status', 'medication', 'subject', 'quantity', 'action'];
    patientId: string | null = null;
    useOrgWide = false;

    backendEndPoint = inject(backendEndPointToken);
    http = inject(HttpClient);
    private medicationDispenseUtilityService = inject(MedicationDispenseUtilityService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);


    canAddDispense$ = this.auth.user.pipe(map(user => this.auth.can('medicationDispense', 'add')));
    canViewAllDispenses$ = this.auth.user.pipe(map(user => this.auth.can('medicationDispense', 'viewAll')));
    private rawMedicationDispenses: MedicationDispense[] = [];
    private dispenseIdFromRoute: string | null = null;
    private autoDialogOpened = false;

    // Avoid re-adding dynamic groups/keys every time showRow/buildDetails is called
    private readonly dynamicGroupKey = '__dynamicGroupsApplied__';

    // Best-effort: name for MedicationDispense.medication[x]
    private getDispensedMedicationLabel(row: MedicationDispense): string {
        const mr = row?.medicationReference as any;
        const mc = row?.medicationCodeableConcept as any;
        return (
            mc?.text ||
            mc?.coding?.[0]?.display ||
            mr?.display ||
            mr?.reference ||
            'N/A'
        );
    }

    private async fetchMedicationDispenseById(id: string | null): Promise<MedicationDispense | null> {
        if (!id || !id.trim()) {
            return null;
        }

        try {
            const url = `${this.backendEndPoint}/MedicationDispense/${encodeURIComponent(id.trim())}`;
            const resource = await firstValueFrom(
                this.http.get<MedicationDispense>(url).pipe(
                    catchError((error) => {
                        console.warn(`MedicationDispense/${id} not found on backend:`, error);
                        return of(null);
                    })
                )
            );
            return resource;
        } catch (error) {
            console.error('Error fetching MedicationDispense by ID:', error);
            return null;
        }
    }

    // Mirrors the dosage formatting strategy you used in MedicineRequestsComponent
    private flattenDosageInstructionValues(dosage: Dosage[] | undefined | null, prefix = 'Dosage'): Record<string, any> {
        const out: Record<string, any> = {};
        const rows = Array.isArray(dosage) ? dosage : [];
        if (!rows.length) return out;

        const clean = (v: any) => (v === undefined || v === null ? '' : String(v).trim());

        const fmtTiming = (d: any): string => {
            const repeat = d?.timing?.repeat ?? {};
            const freq = repeat?.frequency;
            const period = repeat?.period;
            const periodUnit = repeat?.periodUnit;

            if (freq != null && period != null) {
                return `${clean(freq)} time(s) every ${clean(period)} ${clean(periodUnit)}`.trim();
            }
            return '';
        };

        const fmtTimingCodeOnly = (d: any): string =>
            clean(d?.timing?.code?.text) ||
            clean(d?.timing?.code?.coding?.[0]?.display) ||
            clean(d?.timing?.code?.coding?.[0]?.code);

        const fmtDuration = (d: any): string => {
            const repeat = d?.timing?.repeat ?? {};
            const duration = repeat?.duration;
            const durationUnit = repeat?.durationUnit;
            if (duration == null && !durationUnit) return '';
            return `${clean(duration)} ${clean(durationUnit)}`.trim();
        };

        const fmtDose = (d: any): string => {
            const dq = d?.doseAndRate?.[0]?.doseQuantity;
            const value = dq?.value;
            const unit = dq?.unit || dq?.code;
            if (value == null && !unit) return '';
            return `${clean(value)} ${clean(unit)}`.trim();
        };

        rows.forEach((d: any, i) => {
            const n = i + 1;
            const instruction = clean(d?.text);
            const timing = fmtTiming(d);
            const timingCode = fmtTimingCodeOnly(d);
            const duration = fmtDuration(d);
            const dose = fmtDose(d);

            if (instruction) out[`${prefix} ${n} - Instruction`] = instruction;
            if (timing) out[`${prefix} ${n} - Timing`] = timing;
            if (timingCode) out[`${prefix} ${n} - Timing Code`] = timingCode;
            if (duration) out[`${prefix} ${n} - Duration`] = duration;
            if (dose) out[`${prefix} ${n} - Dose`] = dose;
        });

        return out;
    }

    private buildSubstitutionSummary(row: MedicationDispense): Record<string, any> {
        const sub = (row as any)?.substitution;
        if (!sub) return {};

        const out: Record<string, any> = {};

        if (sub?.wasSubstituted !== undefined) out['Substitution - Was substituted'] = String(!!sub.wasSubstituted);
        const type = sub?.type;
        if (type?.text || type?.coding?.length) out['Substitution - Type'] = type?.text || type?.coding?.[0]?.display || type?.coding?.[0]?.code;
        const reason = sub?.reason;
        if (reason?.text || reason?.coding?.length) out['Substitution - Reason'] = reason?.text || reason?.coding?.[0]?.display || reason?.coding?.[0]?.code;

        // Deep-ish nested best-effort
        const responsible = sub?.responsibleParty;
        if (responsible?.display || responsible?.reference) out['Substitution - Responsible party'] = responsible?.display || responsible?.reference;

        return out;
    }

    private ensureGroup(builder: DetailsBuilderObject, groupName: string, groupIcon: string, keys: string[]) {
        const groups = builder.groups || (builder.groups = []);
        const existing = groups.find(g => g.groupName === groupName);
        if (existing) {
            // merge keys without duplicates
            const set = new Set([...(existing.groupKeys || [])]);
            keys.forEach(k => set.add(k));
            existing.groupKeys = Array.from(set);
            return;
        }
        groups.push({ groupName, groupIcon, groupKeys: keys });
    }

    private extractMedicationIdFromReference(ref?: string): string | null {
        const r = (ref ?? '').toString().trim();
        if (!r) return null;
        if (r.startsWith('Medication/')) return r.split('/').pop() || null;
        return null;
    }

    async buildDetails(row: MedicationDispense & Record<string, any>): Promise<DetailsBuilderObject> {
        return new Promise(async (resolve) => {

            const detailsBuilder: DetailsBuilderObject = {
                resourceName: 'Medication Dispense',
                resourceIcon: 'local_pharmacy',
                specialHeader: {
                    strongSectionKey: 'medicationReference',
                    iconSectionKeys: ['status'],
                    contentSectionKeys: ['subject', 'whenHandedOver']
                },
                groups: [
                    { groupName: 'Identification', groupIcon: 'playlist_add_check', groupKeys: ['status', 'category', 'subject', 'authorizingPrescription'] },
                    { groupName: 'Medication', groupIcon: 'medication', groupKeys: ['medicationReference'] },
                    { groupName: 'Logistics', groupIcon: 'calendar_today', groupKeys: ['whenHandedOver', 'quantity', 'receiver'] }
                ]
            };
            let url = ''; let url2 = '';
            let patientId = this.stateService.currentPatientId$.getValue();
            if (patientId) {
                url = `${this.backendEndPoint}/ChargeItem?subject=Patient/${patientId}&_count=1000`;
                url2 = `${this.backendEndPoint}/Invoice?subject=Patient/${patientId}&_count=1000`;
            } else {
                url = `${this.backendEndPoint}/ChargeItem?subject=${row.subject?.reference}&_count=1000`;
                url2 = `${this.backendEndPoint}/Invoice?subject=${row.subject?.reference}&_count=1000`;
            }
            const isPaid = await firstValueFrom(
                forkJoin({
                    cI: this.http.get<any>(`${url}`),
                    Invoice: this.http.get<any>(`${url2}`)
                }).pipe(
                    map((res: { cI: Bundle<ChargeItem>, Invoice: Bundle<Invoice> }) => {
                        const entries = res.cI.entry || [];
                        if (entries.length === 0) {
                            return false;
                        } else {
                            const cItem = entries.find(entry => {
                                const ci = entry.resource;
                                return ci?.service?.find((srv: any) => {
                                    return typeof srv?.reference === 'string' && srv.reference.includes(`MedicationDispense/${row.id}`)
                                        && (ci?.status === 'billable' || ci?.status === 'billed')

                                        ;
                                }) !== undefined
                                // && ci?.productReference?.reference === `Medication/${row.medicationReference?.reference?.split('/').pop()}`
                            });
// $#$                            // /* alert(JSON.stringify(cItem) */);
                            if (cItem) {
                                //check pAYment reconciliation
                                res.Invoice.entry = res.Invoice.entry || [];
                                if (res.Invoice.entry.length === 0) {
                                    return false;
                                }
                                const inv = res.Invoice.entry.find((entry) => {
                                    const invoice = entry.resource;
                                    return invoice?.lineItem?.find((line) => {
                                        return line.chargeItemReference?.reference === `ChargeItem/${cItem.resource?.id}`;

                                    }) !== undefined;
                                });
// $#$                                // /* alert(JSON.stringify(inv) */);
                                if (!inv) {
                                    return false;
                                }
                                if (inv.resource?.status === 'balanced') {
                                    row['isPaid'] = true;
                                    return true;
                                } else {
                                    return false;
                                }

                            } else {
                                return false;
                            }
                        }
                    }))


            )
// $#$            // /* alert(isPaid); */
            if (isPaid) {
                row['isPaid'] = true;
                row['paid status'] = 'Paid';
                if (!detailsBuilder.specialHeader.iconSectionKeys?.includes('paid status')) {
                    detailsBuilder.specialHeader.iconSectionKeys?.push('paid status');
                }
            }

            const dosage = Array.isArray(row?.dosageInstruction) ? row.dosageInstruction : [];
            // if (!dosage.length) return base;

            // Avoid duplicates by checking groupName
            const already = detailsBuilder.groups.some(g => g.groupName === 'Dosage Instructions');
            if (!already && dosage.length) {

                detailsBuilder.groups.push({
                    groupName: 'Dosage Instructions',
                    groupIcon: 'schedule',
                    groupKeys: this.mHelperService.flattenDosageInstructionKeys(dosage)
                });
            }

            // Clone base builder if you already have one elsewhere; otherwise build inline
            // NOTE: replace `this.detailsBuilder` with your existing base builder object if present.
            const base: DetailsBuilderObject = {
                ...detailsBuilder,
                groups: [...((detailsBuilder?.groups) || [])]
            };

            // Guard: only apply dynamic augmentation once per row instance
            // if (!row[this.dynamicGroupKey]) {
            //     row[this.dynamicGroupKey] = true;

            // ----------------------------
            // Dispensed medicine + dosage
            // ----------------------------
            row['dispensed Medicine'] = this.getDispensedMedicationLabel(row);

            const dispensedDosage = Array.isArray((row as any)?.dosageInstruction)
                ? (row as any).dosageInstruction
                : [];
            const dispensedDosageFlat = this.flattenDosageInstructionValues(dispensedDosage, 'Dispensed dosage');
            Object.assign(row, dispensedDosageFlat);

            // ----------------------------
            // Original requested medicine + dosage (from MedicationRequest)
            // ----------------------------
            const authRx = Array.isArray((row as any)?.authorizingPrescription)
                ? (row as any).authorizingPrescription?.[0]
                : (row as any)?.authorizingPrescription;

            const mrId = (authRx?.reference ?? '').toString().startsWith('MedicationRequest/')
                ? (authRx.reference as string).split('/').pop()
                : null;

            let mr: MedicationRequest | null = null;
            if (mrId) {
                try {
                    mr = await firstValueFrom(
                        this.http.get<MedicationRequest>(`${this.backendEndPoint}/MedicationRequest/${mrId}`)
                    );
                } catch {
                    mr = null;
                }
            }

            row['requested Medicine'] =
                mr?.medicationCodeableConcept?.text ||
                mr?.medicationCodeableConcept?.coding?.[0]?.display ||
                mr?.medicationReference?.display ||
                mr?.medicationReference?.reference ||
                'N/A';

            const requestedDosage = Array.isArray(mr?.dosageInstruction) ? mr!.dosageInstruction : [];
            const requestedDosageFlat = this.flattenDosageInstructionValues(requestedDosage, 'Requested dosage');
            Object.assign(row, requestedDosageFlat);

            // ----------------------------
            // Substitution (optional group)
            // ----------------------------
            const subSummary = this.buildSubstitutionSummary(row);
            Object.assign(row, subSummary);

            // ----------------------------
            // Grouping: requested vs dispensed (NO duplicates)
            // ----------------------------
            const requestedKeys = [
                'requested Medicine',
                ...Object.keys(requestedDosageFlat)
            ].filter(Boolean);

            this.ensureGroup(base, 'Original Request (Medication + Dosage)', 'description', requestedKeys);

            const dispensedKeys = [
                'dispensed Medicine',
                ...Object.keys(dispensedDosageFlat)
            ].filter(Boolean);

            this.ensureGroup(base, 'Dispensed (Medication + Dosage)', 'local_pharmacy', dispensedKeys);

            const substitutionKeys = Object.keys(subSummary);
            if (substitutionKeys.length) {
                this.ensureGroup(base, 'Substitution', 'swap_horiz', substitutionKeys);
            }

            // ----------------------------
            // NEW: Dispensed medication core details (ingredients + stock/expiry)
            // Only if dispense is from stock (medicationReference)
            // ----------------------------
            const medId = this.extractMedicationIdFromReference((row as any)?.medicationReference?.reference);
            if (medId) {
                try {
                    const med = await firstValueFrom(
                        this.http.get<Medication>(`${this.backendEndPoint}/Medication/${medId}`)
                    );

                    row['Dispensed - Ingredients'] = this.utilityService.extractMedicationIngredients(med, {
                        includePrefix: false,
                        onlyActive: true
                    }) || 'N/A';

                    const remaining = this.utilityService.getMedicationTotalRemaining(med);
                    row['Dispensed - In stock'] = remaining
                        ? `${remaining.value}${remaining.unit ? ` ${remaining.unit}` : ''}`
                        : 'N/A';

                    row['Dispensed - Expiry date'] = med?.batch?.expirationDate
                        ? new Date(med.batch.expirationDate).toLocaleDateString()
                        : 'N/A';

                    row['Dispensed - Status'] = (med as any)?.status ?? 'N/A';
                    row['Dispensed - Form'] =
                        (med as any)?.form?.text ||
                        (med as any)?.form?.coding?.[0]?.display ||
                        (med as any)?.form?.coding?.[0]?.code ||
                        'N/A';

                    // Add a dedicated group (deduped by ensureGroup)
                    this.ensureGroup(base, 'Dispensed Medication Details', 'science', [
                        'Dispensed - Ingredients',
                        'Dispensed - Form',
                        'Dispensed - Status',
                        // 'Dispensed - In stock',
                        'Dispensed - Expiry date'
                    ]);
                } catch {
                    // If fetch fails, still show group with best-effort placeholders
                    row['Dispensed - Ingredients'] = row['Dispensed - Ingredients'] ?? 'N/A';
                    this.ensureGroup(base, 'Dispensed Medication Details', 'science', [
                        'Dispensed - Ingredients'
                    ]);
                }
            }



            // Optional: remove the old split groups if they exist (best-effort cleanup)
            base.groups = (base.groups || []).filter(g =>
                !['Original Dosage', 'Final Dosage'].includes(g.groupName)
            );

            resolve(base);
        });
    }

    ngOnInit(): void {
        this.medDispenseTableFilterArray.forEach((values, key) => {
            const group = this.fb.group({});
            values.forEach(value => group.addControl(value, new FormControl(false)));
            this.medDispenseFiltersFormControlObject[key] = group;
        });

        this.route.queryParamMap.subscribe(params => {
            const id = params.get('dispenseId');
            this.dispenseIdFromRoute = id;
            if (!id) {
                this.autoDialogOpened = false;
            }
            this.autoOpenDialogFromRoute();
        });

        this.patientId = this.utilityService.getPatientIdFromRoute();
        const resolvedPatientId = this.stateService.currentPatientIdFromResolver.getValue();
        const encounterPatientId = this.stateService.currentEncounter.getValue()?.patientId ?? null;

        if (!resolvedPatientId && !encounterPatientId) {
            if (!this.auth.can('medicationDispense', 'viewAll')) {
                this.errorService.openandCloseError('No patient selected. Please pick a patient to view medication dispenses.');
                return;
            }
            this.useOrgWide = true;
        } else {
            this.patientId = resolvedPatientId ?? encounterPatientId;
        }

        this.tableDataSource.connect = () => this.tableDataLevel2;
        this.observeDispenses();
    }

    private observeDispenses() {
        const stream = this.useOrgWide
            ? this.stateService.orgWideResources.medicationDispenses
            : this.stateService.PatientResources.medicationDispenses;

        stream.subscribe((all: any[]) => {
            const dispenses = (all || [])
                .map(entry => entry.actualResource as MedicationDispense)
                .filter(Boolean)
                // .reverse()
                .filter(dispense => {
                    if (this.useOrgWide || !this.patientId) return true;
                    return dispense.subject?.reference === `Patient/${this.patientId}`;
                });
            console.log(dispenses);
            this.rawMedicationDispenses = dispenses;
            this.tableDataLevel2.next(dispenses);
            this.autoOpenDialogFromRoute();
        });
    }

    private async autoOpenDialogFromRoute(): Promise<void> {
        if (!this.dispenseIdFromRoute || this.autoDialogOpened) {
            return;
        }

        // First try to find in existing data
        let target = this.rawMedicationDispenses.length > 0
            ? this.rawMedicationDispenses.find(d => d.id === this.dispenseIdFromRoute)
            : null;

        if (target) {
            // Found in cache, proceed normally
            this.autoDialogOpened = true;
            setTimeout(() => {
                this.showRow(target);
                void this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { ...this.route.snapshot.queryParams, dispenseId: null },
                    queryParamsHandling: 'merge',
                    replaceUrl: true
                });
            });
            return;
        }

        // Not found in cache, try to fetch individually
        if (this.rawMedicationDispenses.length > 0) { // Only attempt fetch if we have some data loaded
            try {
                const fetched = await this.fetchMedicationDispenseById(this.dispenseIdFromRoute);
                if (fetched) {
                    this.autoDialogOpened = true;
                    setTimeout(() => {
                        this.showRow(fetched);
                        void this.router.navigate([], {
                            relativeTo: this.route,
                            queryParams: { ...this.route.snapshot.queryParams, dispenseId: null },
                            queryParamsHandling: 'merge',
                            replaceUrl: true
                        });
                    });
                }
            } catch (error) {
                console.error('Failed to fetch medication dispense by ID:', error);
            }
        }
    }

    openDispenseDialog(): void {
        this.dialog.open(NewMedicationDispenseComponent, {
            width: '960px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            autoFocus: false
        });
    }
    async buildMedicationDispenseActions(row: MedicationDispense & Record<string, any>): Promise<any[]> {

        const actions: DetailActionButton[] = [{
            //cancel dispense button
            label: 'Cancel Dispense',
            icon: 'cancel',
            key: 'cancelDispense',
            capabilities: [{
                resource: 'medicationDispense',
                action: 'dispense'
            }]

        }];
        const isPaid = row['isPaid'] === true;

        if (isPaid && row.status !== 'completed') {
            //complete dispense button
            actions.push({
                label: 'Complete Dispense',
                icon: 'check_circle',
                key: 'completeDispense',
                capabilities: [{
                    resource: 'medicationDispense',
                    action: 'dispense'
                }]
            });
        }
        // else{
        //     //change t
        // }

        return actions;

    }
    printInvoiceAction: DetailActionButton = {
        label: 'Print Invoice',
        icon: 'receipt_long',
        key: 'printInvoice',
        capabilities: [{
            resource: 'invoice',
            action: 'read'
        }]

    }
    async showRow(row: MedicationDispense): Promise<void> {
        const toWait = await this.buildDetails(row);
        const toWait2 = await this.buildMedicationDispenseActions(row);
// $#$        // /* alert(JSON.stringify(toWait2) */);
        const dialogRef = this.dialog.open(DetailzViewzComponent, {
            maxHeight: '93vh',
            maxWidth: '90vh',
            data: {
                resourceData: this.buildResourceData(row),
                detailsBuilderObject: toWait,
                ...(['cancelled', 'entered-in-error', 'stopped', 'declined'].includes((row.status ?? '').toString().toLowerCase()) ? {

                }
                    : { actionButtons: toWait2 })
            }
        });

        const sub = dialogRef.componentInstance?.actionInvoked.subscribe(({ key }) => {
            this.handleDetailAction(key, row as any);
        });

        dialogRef.afterClosed().subscribe(() => sub?.unsubscribe());
    }

    private handleDetailAction(actionKey: string, row: (MedicationDispense & Record<string, any>)): void {
        switch (actionKey) {
            case 'cancelDispense':
                this.cancelDispense(row);
                return;
            case 'completeDispense':
                this.completeDispense(row);
                return;
            default:
                return;
        }
    }

    private async cancelDispense(row: MedicationDispense & Record<string, any>): Promise<void> {
        if (!row?.id) {
            this.errorService.openandCloseError('MedicationDispense id is missing.');
            return;
        }

        const status = (row.status ?? '').toString().toLowerCase();
        if (['cancelled', 'entered-in-error'].includes(status)) {
            this.errorService.openandCloseError('Dispense is already cancelled.');
            return;
        }

        const confirmed = await this.confirmDispenseCancellation(row);
        if (!confirmed) {
            return;
        }

        this.medicationDispenseUtilityService.submitDispenseCancellationTransactionByDispenseId(String(row.id));
    }

    private confirmDispenseCancellation(row: MedicationDispense & Record<string, any>): Promise<boolean> {
        const medicationLabel = this.formatMedication(row);
        const quantityLabel = this.formatQuantity(row);
        const safeMedication = medicationLabel === 'N/A' ? 'this medication' : medicationLabel;
        const safeQuantity = quantityLabel === 'N/A' ? 'the recorded quantity' : quantityLabel;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '520px',
            data: {
                title: 'Cancel this dispense?',
                messagesArray: [
                    `The dispensed amount (${safeQuantity}) will be put back into ${safeMedication} inventory.`,
                    'Any paid invoice will be reversed and a refund/credit note will be issued.',
                    'Use this only if the patient never received the medication or if the patient received the wrong medication and is returning it; this action cannot be undone.'
                ],
                confirmText: 'Cancel dispense',
                cancelText: 'Keep dispense',
                icon: 'inventory_2',
                iconColor: 'var(--mat-sys-primary)'
            }
        });

        return firstValueFrom(dialogRef.afterClosed()).then(result => !!result);
    }

    private completeDispense(row: MedicationDispense & Record<string, any>): void {
        if (!row?.id) {
            this.errorService.openandCloseError('MedicationDispense id is missing.');
            return;
        }

        // Enforce payment: UI already hides the button, but keep server action safe.
        const isPaid = row['isPaid'] === true;
        if (!isPaid) {
            this.errorService.openandCloseError('Payment required to complete dispense.');
            return;
        }

        this.medicationDispenseUtilityService.submitDispenseCompletionTransactionByDispenseId(String(row.id));
    }
    buildResourceData(row: MedicationDispense): MedicationDispense & Record<string, any> {
        const dosage = Array.isArray(row?.dosageInstruction) ? row.dosageInstruction : [];
        if (!dosage.length) return row as any;

        // Keep original row but add flattened keys for DetailzViewz consumption
        const flattened = this.mHelperService.flattenDosageInstructionValues(dosage);
        return { ...(row as any), ...flattened };
    }
    mHelperService = inject(MedicationDispenseFieldFilterService);

    formatMedication(row: MedicationDispense): string {
        return row.medicationCodeableConcept?.text
            || row.medicationCodeableConcept?.coding?.[0]?.display
            || row.medicationReference?.display
            || row.medicationReference?.reference
            || 'N/A';
    }

    formatQuantity(row: MedicationDispense): string {
        const quantity = row.quantity;
        if (!quantity) return 'N/A';
        const value = quantity.value ?? '';
        const unit = quantity.unit ?? '';
        return `${value}${unit ? ` ${unit}` : ''}`;
    }
}
